import {
  groupByCreative,
  groupByCreativeSize,
  weightedCtr,
} from "@/pages/CampaignOverviewPage/utils/creativeGrouping";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

// A creative, size, browser or segment with only a handful of impressions can
// top a CTR ranking on noise alone, so only items at or above this volume can
// be named "top". Product hasn't set the real number yet (NP-1419 open item 1).
export const TOP_PERFORMER_MIN_IMPRESSIONS = 1000;

interface TopPerformer {
  // Set for creatives: the group key that tells same-named creatives apart.
  key?: string;
  // CTR as a percentage (0.62 means 0.62%).
  ctr: number;
  label: string;
}

export interface TopPerformers {
  browser: TopPerformer | null;
  creative: TopPerformer | null;
  segment: TopPerformer | null;
  size: TopPerformer | null;
}

const UNKNOWN_BROWSER = TEXT.campaignOverview.labels.other;

const highestCtr = (candidates: TopPerformer[]): TopPerformer | null =>
  candidates.reduce<TopPerformer | null>(
    (best, candidate) => (!best || candidate.ctr > best.ctr ? candidate : best),
    null
  );

const deriveTopCreative = (rows: PerformanceReportRow[]) =>
  highestCtr(
    groupByCreative(rows)
      .filter(({ impressions }) => impressions >= TOP_PERFORMER_MIN_IMPRESSIONS)
      .map(({ clicks, impressions, key, name }) => ({
        ctr: weightedCtr(clicks, impressions),
        key,
        label: name,
      }))
  );

const deriveTopSize = (rows: PerformanceReportRow[]) =>
  highestCtr(
    groupByCreativeSize(rows)
      .filter(({ impressions }) => impressions >= TOP_PERFORMER_MIN_IMPRESSIONS)
      .map(({ clicks, impressions, size }) => ({
        ctr: weightedCtr(clicks, impressions),
        label: size,
      }))
  );

// Browser rows are device × OS × browser combinations, so clicks and
// impressions are summed per browser before CTR is taken. Rows with no
// browser are skipped — "Other" isn't a browser that can be "top".
const deriveTopBrowser = (rows: PerformanceReportRow[]) => {
  const totals = rows.reduce((acc, { browser, clicks, impressions }) => {
    if (!browser || browser === UNKNOWN_BROWSER) return acc;
    const existing = acc.get(browser) ?? { clicks: 0, impressions: 0 };
    return acc.set(browser, {
      clicks: existing.clicks + (clicks ?? 0),
      impressions: existing.impressions + (impressions ?? 0),
    });
  }, new Map<string, { clicks: number; impressions: number }>());

  return highestCtr(
    [...totals.entries()]
      .filter(
        ([, { impressions }]) => impressions >= TOP_PERFORMER_MIN_IMPRESSIONS
      )
      .map(([label, { clicks, impressions }]) => ({
        ctr: weightedCtr(clicks, impressions),
        label,
      }))
  );
};

interface SegmentTotals {
  clicks: number;
  hasCtr: boolean;
  impressions: number;
}

// A segment can appear on more than one report row (per vendor, campaign or
// day), so clicks and impressions are summed per segment before CTR is taken —
// the same way creatives, sizes and browsers are judged. A segment whose rows
// all report no CTR has no CTR. Rows with no segment name are left out.
const totalsBySegment = (
  rows: PerformanceReportRow[]
): Map<string, SegmentTotals> =>
  rows.reduce(
    (acc, { audience_segment_name: label, clicks, ctr, impressions }) => {
      if (!label) return acc;
      const existing = acc.get(label) ?? {
        clicks: 0,
        hasCtr: false,
        impressions: 0,
      };
      return acc.set(label, {
        clicks: existing.clicks + (clicks ?? 0),
        hasCtr: existing.hasCtr || (ctr !== null && ctr !== undefined),
        impressions: existing.impressions + (impressions ?? 0),
      });
    },
    new Map<string, SegmentTotals>()
  );

const deriveTopSegment = (rows: PerformanceReportRow[]) => {
  const totals = totalsBySegment(rows);

  return highestCtr(
    [...totals.entries()]
      .filter(
        ([, { hasCtr, impressions }]) =>
          hasCtr && impressions >= TOP_PERFORMER_MIN_IMPRESSIONS
      )
      .map(([label, { clicks, impressions }]) => ({
        ctr: weightedCtr(clicks, impressions),
        label,
      }))
  );
};

export const deriveTopPerformers = ({
  audienceRows,
  creativeRows,
  deviceBrowserRows,
}: {
  audienceRows: PerformanceReportRow[];
  creativeRows: PerformanceReportRow[];
  deviceBrowserRows: PerformanceReportRow[];
}): TopPerformers => ({
  browser: deriveTopBrowser(deviceBrowserRows),
  creative: deriveTopCreative(creativeRows),
  segment: deriveTopSegment(audienceRows),
  size: deriveTopSize(creativeRows),
});

// The totals a row is ranked on: its segment's, so every row of a segment sorts
// together and the first row is the segment the tile names. A row with no
// segment name stands on its own numbers.
const rankingTotals = (
  row: PerformanceReportRow,
  totals: Map<string, SegmentTotals>
): SegmentTotals =>
  totals.get(row.audience_segment_name ?? "") ?? {
    clicks: row.clicks ?? 0,
    hasCtr: row.ctr !== null && row.ctr !== undefined,
    impressions: row.impressions ?? 0,
  };

// Segments that can be named "top" (enough impressions, a CTR) rank first, then
// segments with a CTR but too little volume, then segments with no CTR.
const rankTier = ({ hasCtr, impressions }: SegmentTotals): number => {
  if (!hasCtr) return 2;
  return impressions >= TOP_PERFORMER_MIN_IMPRESSIONS ? 0 : 1;
};

// Ranks audience segments for the table's default order: by the segment's tier,
// then its CTR highest first, with ties broken on its impressions. Rows of one
// segment then follow each other, highest row CTR first.
export const rankSegmentsByCtr = (
  rows: PerformanceReportRow[]
): PerformanceReportRow[] => {
  const totals = totalsBySegment(rows);
  return [...rows].sort((rowA, rowB) => {
    const totalsA = rankingTotals(rowA, totals);
    const totalsB = rankingTotals(rowB, totals);
    return (
      rankTier(totalsA) - rankTier(totalsB) ||
      weightedCtr(totalsB.clicks, totalsB.impressions) -
        weightedCtr(totalsA.clicks, totalsA.impressions) ||
      totalsB.impressions - totalsA.impressions ||
      (rowB.ctr ?? 0) - (rowA.ctr ?? 0) ||
      (rowB.impressions ?? 0) - (rowA.impressions ?? 0)
    );
  });
};
